/**
 * Shared styles for all form input components (FormInputBase children)
 *
 * Used by: wf-input, wf-email, wf-textarea, wf-field, wf-number
 */

import { css } from 'lit';

export const formInputBaseStyles = css`
  :host {
    display: block;
  }

  :host([hidden]) {
    display: none;
  }

  .wf-field-container {
    display: flex;
    flex-direction: column;
  }

  .wf-label {
    font-size: var(--wf-font-size-sm);
    font-weight: var(--wf-font-weight-label);
    color: var(--wf-color-text);
    margin-bottom: var(--wf-spacing-2);
  }

  .wf-label-required::after {
    content: ' *';
    color: var(--wf-color-error);
  }

  /* Error wrapper for animated expand/collapse */
  .wf-error-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 200ms ease-out;
  }

  .wf-error-wrapper.wf-has-error {
    grid-template-rows: 1fr;
  }

  .wf-error-message {
    overflow: hidden;
    min-height: 0;
    font-size: var(--wf-font-size-sm);
    color: var(--wf-color-error);
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 200ms ease-out, transform 200ms ease-out;
  }

  .wf-has-error .wf-error-message {
    margin-top: var(--wf-spacing-2);
    opacity: 1;
    transform: translateY(0);
  }

  .wf-hint {
    font-size: var(--wf-font-size-sm);
    color: var(--wf-color-text-muted);
    margin-top: var(--wf-spacing-2);
  }

  .wf-input {
    width: 100%;
    min-height: var(--wf-input-min-height);
    padding: var(--wf-spacing-4);
    font-size: var(--wf-font-size-base);
    font-weight: var(--wf-font-weight-input);
    font-family: inherit;
    background: var(--wf-glass-bg);
    backdrop-filter: blur(var(--wf-glass-blur));
    -webkit-backdrop-filter: blur(var(--wf-glass-blur));
    border: 1px solid var(--wf-glass-border);
    border-radius: var(--wf-radius-md);
    box-shadow: var(--wf-glass-shadow);
    color: var(--wf-color-text);
    transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
    outline: none;
    box-sizing: border-box;
  }

  .wf-input::placeholder {
    color: var(--wf-color-text-muted);
    font-weight: var(--wf-font-weight-input);
  }

  .wf-input:focus {
    border-color: var(--wf-color-primary);
    box-shadow: 0 0 0 var(--wf-focus-ring-width) var(--wf-focus-ring-primary);
  }

  .wf-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .wf-input.wf-input-error {
    border-color: var(--wf-color-error);
  }

  .wf-input.wf-input-error:focus {
    box-shadow: 0 0 0 var(--wf-focus-ring-width) var(--wf-focus-ring-error);
  }

  /* Textarea-specific styles */
  .wf-textarea {
    width: 100%;
    padding: var(--wf-spacing-4);
    font-size: var(--wf-font-size-base);
    font-family: inherit;
    background: var(--wf-glass-bg);
    backdrop-filter: blur(var(--wf-glass-blur));
    -webkit-backdrop-filter: blur(var(--wf-glass-blur));
    border: 1px solid var(--wf-glass-border);
    border-radius: var(--wf-radius-lg);
    box-shadow: var(--wf-glass-shadow);
    color: var(--wf-color-text);
    transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
    outline: none;
    box-sizing: border-box;
    resize: vertical;
    min-height: var(--wf-textarea-min-height);
  }

  .wf-textarea::placeholder {
    color: var(--wf-color-text-muted);
  }

  .wf-textarea:focus {
    border-color: var(--wf-color-primary);
    box-shadow: 0 0 0 var(--wf-focus-ring-width) var(--wf-focus-ring-primary);
  }

  .wf-textarea:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .wf-textarea.wf-textarea-error {
    border-color: var(--wf-color-error);
  }

  .wf-textarea.wf-textarea-error:focus {
    box-shadow: 0 0 0 var(--wf-focus-ring-width) var(--wf-focus-ring-error);
  }

  .wf-char-count {
    font-size: var(--wf-font-size-sm);
    color: var(--wf-color-text-muted);
    text-align: right;
  }

  .wf-char-count.wf-char-limit {
    color: var(--wf-color-error);
  }

  /* wf-field slotted input styles */
  .wf-input-wrapper {
    position: relative;
  }

  /* Number input - hide spinners */
  .wf-input::-webkit-outer-spin-button,
  .wf-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .wf-input[type='number'] {
    -moz-appearance: textfield;
  }
`;
